import { CalendarSharingDetailLevels } from './CalendarSharingPermissionsUtils';
import createPublishingPermissionService from '../services/createPublishingPermissionService';
import CalendarPublishingError from '../store/schema/CalendarPublishingError';
import getStore from 'owa-calendar-sharing/lib/store/publishingStore';
import SetCalendarPublishingResponse from 'owa-service/lib/contract/SetCalendarPublishingResponse';
import {
    setCalendarPublishingErrorStateError,
    setCalendarPublishingCalendarError,
    updatePublishSelectedCalendar,
    saveCalendarPublishingPermissions,
    updateSelectedPublishingPermission,
    loadCalendarPublishingPermissions,
} from '../actions/calendarPublishing';
import { isConsumer } from 'owa-session-store';

export const DefaultPublishDetailLevels = ['AvailabilityOnly', 'LimitedDetails', 'FullDetails'];
export const ConsumerDefaultPublishDetailLevels = ['AvailabilityOnly', 'FullDetails'];

export async function createCalendarPublishingPermission(
    folderId: string,
    detailLevel: string,
    publishCalendar?: boolean,
    resetUrl?: boolean
): Promise<boolean> {
    let viewState = getStore().viewState;

    // Reset Publishing errors
    setCalendarPublishingErrorStateError(null);
    setCalendarPublishingCalendarError(folderId, null);

    let data: SetCalendarPublishingResponse = await createPublishingPermissionService(
        folderId,
        detailLevel,
        publishCalendar,
        resetUrl
    );
    if (data) {
        let { BrowseUrl, ICalUrl, CurrentDetailLevel } = data;

        // break the observables on the shallow copy before updating it in the save
        let updatedCalendar = { ...viewState.publishedCalendarsMap.get(folderId) };
        updatedCalendar.itemStates = updatedCalendar.itemStates.map(itemState => {
            let newState = { ...itemState };
            newState.isResetLinksPending = false;
            newState.isUnpublishPending = false;
            newState.CurrentDetailLevel = CurrentDetailLevel;
            newState.BrowseUrl = BrowseUrl;
            newState.ICalUrl = ICalUrl;
            return newState;
        });

        saveCalendarPublishingPermissions(
            updatedCalendar.itemStates,
            folderId,
            updatedCalendar.calName,
            updatedCalendar.primaryColor
        );
        updatePublishSelectedCalendar(null);
        updateSelectedPublishingPermission(null);

        if (isConsumer()) {
            // when issuing a consumer request to create a new publish permission, the response does not include the new data, so we
            // have to issue a full refresh.
            loadCalendarPublishingPermissions(
                folderId,
                '',
                updatedCalendar.primaryColor,
                updatedCalendar.calName
            );
        }

        return Promise.resolve(true);
    } else {
        if (detailLevel != 'Disabled' && publishCalendar != false) {
            setCalendarPublishingErrorStateError(
                CalendarPublishingError.CreatePublishingPermissionFailure
            );
        } else {
            setCalendarPublishingCalendarError(folderId, CalendarPublishingError.UnpublishFailure);
        }

        return Promise.resolve(false);
    }
}

export function getAvailablePublishingDetailLevelsFromMaxLevel(maxDetailLevel: string) {
    let maxDetailIndex = CalendarSharingDetailLevels.indexOf(maxDetailLevel);
    if (maxDetailIndex >= 0) {
        return CalendarSharingDetailLevels.filter(
            (item: string, index: number) => index <= maxDetailIndex
        );
    } else {
        return DefaultPublishDetailLevels;
    }
}

export function getConsumerAvailablePublishingDetailLevelsFromMaxLevel(maxDetailLevel: string) {
    let maxDetailIndex = CalendarSharingDetailLevels.indexOf(maxDetailLevel);
    if (maxDetailIndex >= 0) {
        return CalendarSharingDetailLevels.filter(
            (item: string, index: number) =>
                index <= maxDetailIndex && ConsumerDefaultPublishDetailLevels.includes(item)
        );
    } else {
        return DefaultPublishDetailLevels;
    }
}

/**
 * Consumer publishing details always have AvailabilityOnly and FullDetails
 */
export function getConsumerAvailablePublishingDetailLevels() {
    return ConsumerDefaultPublishDetailLevels;
}
